import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { RouteBuilderAreaRoutes } from '../../RouteBuilderAreaRoutes'
import {
  type SavedTripsTab,
  useOptionalSavedTripsTabs,
} from './SavedTripsTabsContext'

const EDITOR_SEGMENTS = ['create', 'create-from-vtr', 'edit', 'assign']

// Non-routing header tab, styled to match the hulk-trips top-bar tabs: bold
// label, secondary color when inactive, accent color + 2px accent underline when
// active. We render our own button (rather than the shared MoiaTabButton) so the
// active underline reliably shows in the header row.
const tabButtonStyle =
  (isActive: boolean) =>
  (theme: {
    semantic: {
      ColorActionSecondaryDefault: string
      ColorActionSecondaryHovered: string
      ColorAccentDefault: string
    }
  }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap' as const,
    height: '100%',
    padding: 0,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '25.6px',
    borderBottom: `2px solid ${isActive ? theme.semantic.ColorAccentDefault : 'transparent'}`,
    color: isActive
      ? theme.semantic.ColorAccentDefault
      : theme.semantic.ColorActionSecondaryDefault,
    '&:hover': {
      color: isActive
        ? theme.semantic.ColorAccentDefault
        : theme.semantic.ColorActionSecondaryHovered,
    },
  })

/**
 * Saved / Archived tabs rendered in the Route Builder top-bar header, mirroring
 * the hulk-trips Vehicles/Templates tabs. Tab selection lives in
 * {@link SavedTripsTabsProvider}; SavedTripsView reads the selection. Clicking a
 * tab while inside the editor navigates back to the saved-routes list so the
 * selected tab is visible.
 */
export const SavedTripsHeaderTabs = () => {
  const { t } = useTranslation()
  const tabs = useOptionalSavedTripsTabs()
  const navigate = useNavigate()
  const location = useLocation()

  if (!tabs) return null

  const { activeTab, setActiveTab } = tabs

  const inEditor = location.pathname
    .split('/')
    .filter(Boolean)
    .some(segment => EDITOR_SEGMENTS.includes(segment))

  const handleSelect = (tab: SavedTripsTab) => {
    setActiveTab(tab)
    if (inEditor) navigate(RouteBuilderAreaRoutes.SavedTrips.route)
  }

  return (
    <div css={{ display: 'flex', gap: 24, height: '100%' }}>
      <button
        type="button"
        css={tabButtonStyle(activeTab === 'saved')}
        onClick={() => handleSelect('saved')}
      >
        {t('routeBuilder_savedTripsTabs_saved')}
      </button>
      <button
        type="button"
        css={tabButtonStyle(activeTab === 'archived')}
        onClick={() => handleSelect('archived')}
      >
        {t('routeBuilder_savedTripsTabs_archived')}
      </button>
    </div>
  )
}
